import { useCallback, useEffect, useMemo, useState } from 'react';

import useToast from '@/components/Toast/useToast';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { FeedbackDiff, useFeedbackCrud } from '@/hooks/useFeedbackCrud';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeedbackConfigs, useFeedbacks } from '@/hooks/useSwr';
import {
  AnnotationQueueRubricItem,
  AnnotationQueueSchema,
  FeedbackConfigSchema,
  FeedbackSchema,
} from '@/types/schema';

/* -------------------------------------------------------------------------- */

/*  NEW REFACTORED RUBRIC FORM (wraps BaseQueueRubricForm)                    */

/* -------------------------------------------------------------------------- */
import { BaseQueueRubricForm, ItemRendererParams } from './BaseQueueRubricForm';
import {
  ExpandableComponent,
  FeedbackHeader,
  SingleFeedbackCategoryChoice,
  SingleFreeformNumberInput,
  SingleFreeformTextInput,
} from './FeedbackRubricShared';

const SingleFeedbackRubricForm = (props: {
  rubricItem: AnnotationQueueRubricItem;
  runId: string;
  sessionId: string;
  feedbackConfig: FeedbackConfigSchema;
  feedback?: FeedbackSchema;
  autoFocus?: boolean;
  onSuccess: (index: number, deleted: boolean) => void;
  index: number;
  feedbackFormExpanded: Array<boolean>;
  setFeedbackFormExpanded: React.Dispatch<React.SetStateAction<Array<boolean>>>;
  isLoading: boolean;
  runRuleId?: string;
}) => {
  const { createToast } = useToast();
  const {
    feedback,
    onSubmit: _onSubmit,
    onDelete,
    isMutating,
  } = useFeedbackCrud({
    runId: props.runId,
    feedback_key: props.feedbackConfig.feedback_key,
    existingFeedback: props.feedback,
    onSuccess: (del) => {
      props.onSuccess(props.index, del);
    },
    onError: (error) => {
      createToast({
        title: 'Failed to update feedback',
        description: error.message,
        error: true,
      });
    },
    runRuleId: props.runRuleId,
  });
  const [loadingCategory, setLoadingCategory] = useState<string | undefined>(
    undefined
  );

  const [queuedValue, setQueuedValue] = useState<FeedbackDiff | null>(null);

  useEffect(() => {
    if (!props.isLoading && queuedValue) {
      if (props.feedback) {
        createToast({
          title: 'Feedback already exists',
          description: 'Please refresh and try again.',
          error: true,
        });
      } else {
        _onSubmit(queuedValue);
      }
      setQueuedValue(null);
    }
  }, [props.isLoading, queuedValue, props.feedback, createToast, _onSubmit]);

  const onSubmit = useCallback(
    (value: FeedbackDiff) => {
      if (props.isLoading) {
        setQueuedValue(value);
        return;
      }

      if (!isMutating) {
        _onSubmit(value);
      }
    },
    [_onSubmit, isMutating, props.isLoading]
  );

  const selectedCategory = feedback?.value ?? feedback?.score;
  const showCategories = Array.isArray(
    props.feedbackConfig?.feedback_config.categories
  )
    ? props.feedbackConfig.feedback_config.categories.length > 0
    : false;

  return (
    props.rubricItem.feedback_key && (
      <ExpandableComponent
        key={props.index}
        expandedComponent={
          <div className="flex flex-col gap-3">
            {props.rubricItem.description && (
              <div className="text-sm text-quaternary">
                {props.rubricItem.description}
              </div>
            )}
            {showCategories ? (
              <div className="flex flex-col gap-1">
                {Array.isArray(
                  props.feedbackConfig?.feedback_config.categories
                ) &&
                  props.feedbackConfig?.feedback_config.categories.map(
                    (category, categoryIndex) => {
                      const categoryLabel = category.label ?? category.value;
                      const categoryDescription = category.label
                        ? props.rubricItem.value_descriptions?.[categoryLabel]
                        : props.rubricItem.score_descriptions?.[
                            `${categoryLabel}`
                          ];
                      return (
                        <SingleFeedbackCategoryChoice
                          key={categoryIndex}
                          loading={
                            (isMutating && loadingCategory === categoryLabel) ||
                            (!!queuedValue &&
                              (queuedValue.value ?? queuedValue.score) ===
                                categoryLabel)
                          }
                          categoryName={categoryLabel}
                          description={categoryDescription}
                          selected={categoryLabel === selectedCategory}
                          onSelect={() => {
                            if (categoryLabel === selectedCategory) {
                              onDelete();
                            } else {
                              setLoadingCategory(categoryLabel);
                              onSubmit({
                                value: category.label,
                                score: category.value,
                              });
                            }
                          }}
                        />
                      );
                    }
                  )}
              </div>
            ) : props.feedbackConfig.feedback_config.type === 'freeform' ? (
              <SingleFreeformTextInput
                value={feedback?.comment ?? null}
                onChange={(value) => {
                  onSubmit({
                    comment: value,
                  });
                }}
                autoFocus={props.autoFocus}
                onDeleted={onDelete}
                feedbackConfig={props.feedbackConfig}
              />
            ) : (
              <SingleFreeformNumberInput
                value={feedback?.score}
                onChange={(value) => {
                  onSubmit({
                    score: value,
                  });
                }}
                autoFocus={props.autoFocus}
                onDeleted={onDelete}
                feedbackConfig={props.feedbackConfig}
              />
            )}
          </div>
        }
        overrideExpanded={props.feedbackFormExpanded[props.index]}
        overrideSetExpanded={(expanded) => {
          props.setFeedbackFormExpanded((prev) => {
            const newExpanded = [...prev];
            newExpanded[props.index] = expanded;
            return newExpanded;
          });
        }}
        headerComponent={
          <FeedbackHeader
            feedbackKey={props.rubricItem.feedback_key}
            feedback={feedback}
            isMutating={isMutating}
          />
        }
        collapsedHeaderComponent={
          <FeedbackHeader
            feedbackKey={props.rubricItem.feedback_key}
            feedback={feedback}
            isMutating={isMutating}
            preview={feedback?.comment}
          />
        }
        className="rounded-lg border border-secondary p-3"
      />
    )
  );
};

export const AnnotationQueueRubricForm = (props: {
  annotationQueue: AnnotationQueueSchema;
  runId: string;
  sessionId: string;
  onFeedbackGiven: () => void;
}) => {
  const { annotationQueue, runId, sessionId, onFeedbackGiven } = props;

  const { authorize } = usePermissions();
  const canUpdate = authorize('annotation-queues:update');

  const { session: authSession } = useAuth();

  const feedbackConfigs = useFeedbackConfigs();
  const {
    data: feedbacks,
    isLoading: isLoadingFeedbacks,
    mutate: mutateFeedbacks,
  } = useFeedbacks({
    run: runId,
    session: sessionId,
    user: authSession?.user.id ?? undefined,
  });

  // Build feedback map keyed by feedback_key
  const feedbackMap = useMemo(() => {
    const map: Record<string, FeedbackSchema> = {};
    (feedbacks ?? []).forEach((fb) => {
      map[fb.key] = fb;
    });
    return map;
  }, [feedbacks]);

  /* ---------------------- per-item renderer --------------------------- */
  const itemRenderer = useCallback(
    ({
      rubricItem,
      feedbackConfigs,
      feedback,
      index,
      autoFocus,
      onAfterSubmit,
      feedbackFormExpanded,
      setFeedbackFormExpanded,
      isLoading,
    }: ItemRendererParams<FeedbackSchema>) => {
      const feedbackConfig = feedbackConfigs?.find(
        (c) => c.feedback_key === rubricItem.feedback_key
      );
      if (!feedbackConfig) {
        return null;
      }
      return (
        <SingleFeedbackRubricForm
          rubricItem={rubricItem}
          runId={runId}
          sessionId={sessionId}
          feedbackConfig={feedbackConfig}
          feedback={feedback as FeedbackSchema | undefined}
          autoFocus={autoFocus}
          onSuccess={(idx: number, del: boolean) => {
            mutateFeedbacks();
            onFeedbackGiven();
            onAfterSubmit(idx, del);
          }}
          index={index}
          feedbackFormExpanded={feedbackFormExpanded as boolean[]}
          setFeedbackFormExpanded={setFeedbackFormExpanded}
          isLoading={isLoading}
          runRuleId={annotationQueue.run_rule_id}
        />
      );
    },
    [
      runId,
      sessionId,
      mutateFeedbacks,
      onFeedbackGiven,
      annotationQueue.run_rule_id,
    ]
  );

  return (
    <BaseQueueRubricForm<FeedbackSchema>
      annotationQueue={annotationQueue}
      runId={runId}
      sessionId={sessionId}
      feedbackMap={feedbackMap}
      isLoading={isLoadingFeedbacks}
      canUpdate={canUpdate}
      feedbackConfigs={feedbackConfigs}
      onFeedbackGiven={onFeedbackGiven}
      itemRenderer={itemRenderer}
    />
  );
};
